import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/lib/database.types';

type Account = Database['public']['Tables']['accounts']['Row'];

interface SparklinePoint {
  date: string;
  value: number;
}

interface AccountSparklineData {
  points: SparklinePoint[];
  trend: 'up' | 'down' | 'neutral';
  changePercent: number;
  changeAmount: number;
}

/**
 * Fetches daily balance data for a single account to create sparkline charts.
 * Returns data for the last 6 months by default.
 */
export function useAccountSparkline(account: Account | null, periodDays: number = 180) {
  const { user } = useAuth();
  const [data, setData] = useState<AccountSparklineData | null>(null);
  const [loading, setLoading] = useState(true);
  const abortRef = useRef(0);

  useEffect(() => {
    if (!user || !account) {
      setData(null);
      setLoading(false);
      return;
    }

    const requestId = ++abortRef.current;
    const isStale = () => requestId !== abortRef.current;

    const fetchData = async () => {
      setLoading(true);

      try {
        const today = new Date();
        const from = new Date(today);
        from.setDate(from.getDate() - periodDays);
        const toStr = today.toISOString().split('T')[0];
        const fromStr = from.toISOString().split('T')[0];

        // Try RPC first for performance
        const { data: rpcData } = await supabase.rpc('get_daily_balance_summary', {
          p_account_ids: [account.id],
          p_from: fromStr,
          p_to: toStr,
        });

        if (isStale()) return;

        let points: SparklinePoint[] = [];

        if (rpcData && rpcData.length > 0) {
          const dayMap = new Map<string, number>();
          for (const row of rpcData as {
            out_account_id: string;
            out_day: string;
            out_balance: number;
          }[]) {
            const dayStr =
              typeof row.out_day === 'string'
                ? row.out_day
                : new Date(row.out_day).toISOString().split('T')[0];
            dayMap.set(dayStr, Number(row.out_balance));
          }

          points = [...dayMap.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, value]) => ({ date, value: Math.round(value * 100) / 100 }));
        } else {
          // Fallback: create flat line from current balance
          let currentDate = fromStr;
          while (currentDate <= toStr) {
            points.push({ date: currentDate, value: account.balance });
            const d = new Date(currentDate + 'T12:00:00Z');
            d.setUTCDate(d.getUTCDate() + 1);
            currentDate = d.toISOString().split('T')[0];
          }
        }

        // Downsample to ~30 points for sparkline
        const sampledPoints = downsample(points, 30);

        // Calculate trend
        const firstValue = sampledPoints.length > 0 ? sampledPoints[0].value : account.balance;
        const lastValue =
          sampledPoints.length > 0
            ? sampledPoints[sampledPoints.length - 1].value
            : account.balance;
        const changeAmount = lastValue - firstValue;
        const changePercent = firstValue !== 0 ? (changeAmount / Math.abs(firstValue)) * 100 : 0;
        const trend: 'up' | 'down' | 'neutral' =
          changeAmount > 0.01 ? 'up' : changeAmount < -0.01 ? 'down' : 'neutral';

        setData({
          points: sampledPoints,
          trend,
          changePercent: Math.round(changePercent * 10) / 10,
          changeAmount: Math.round(changeAmount * 100) / 100,
        });
      } catch (error) {
        console.error('Error fetching account sparkline:', error);
        // Fallback to flat line
        setData({
          points: [{ date: new Date().toISOString().split('T')[0], value: account.balance }],
          trend: 'neutral',
          changePercent: 0,
          changeAmount: 0,
        });
      } finally {
        if (!isStale()) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [user, account?.id, account?.balance, periodDays]);

  return { data, loading };
}

function downsample(points: SparklinePoint[], targetCount: number): SparklinePoint[] {
  if (points.length <= targetCount) return points;

  const step = Math.ceil(points.length / targetCount);
  const sampled: SparklinePoint[] = [];
  for (let i = 0; i < points.length; i += step) {
    sampled.push(points[i]);
  }
  // Always include the last point
  if (sampled.length > 0 && sampled[sampled.length - 1].date !== points[points.length - 1].date) {
    sampled.push(points[points.length - 1]);
  }
  return sampled;
}

/**
 * Hook to fetch sparkline data for multiple accounts at once.
 * More efficient than calling useAccountSparkline multiple times.
 */
export function useMultipleAccountSparklines(accounts: Account[], periodDays: number = 180) {
  const { user } = useAuth();
  const [data, setData] = useState<Map<string, AccountSparklineData>>(new Map());
  const [loading, setLoading] = useState(true);
  const abortRef = useRef(0);

  useEffect(() => {
    if (!user || accounts.length === 0) {
      setData(new Map());
      setLoading(false);
      return;
    }

    const requestId = ++abortRef.current;
    const isStale = () => requestId !== abortRef.current;

    const fetchData = async () => {
      setLoading(true);

      try {
        const today = new Date();
        const from = new Date(today);
        from.setDate(from.getDate() - periodDays);
        const toStr = today.toISOString().split('T')[0];
        const fromStr = from.toISOString().split('T')[0];
        const accountIds = accounts.map((a) => a.id);

        const { data: rpcData } = await supabase.rpc('get_daily_balance_summary', {
          p_account_ids: accountIds,
          p_from: fromStr,
          p_to: toStr,
        });

        if (isStale()) return;

        const result = new Map<string, AccountSparklineData>();

        // Group data by account
        const accountDataMap = new Map<string, Map<string, number>>();
        for (const accId of accountIds) {
          accountDataMap.set(accId, new Map());
        }

        if (rpcData && rpcData.length > 0) {
          for (const row of rpcData as {
            out_account_id: string;
            out_day: string;
            out_balance: number;
          }[]) {
            const dayStr =
              typeof row.out_day === 'string'
                ? row.out_day
                : new Date(row.out_day).toISOString().split('T')[0];
            const accMap = accountDataMap.get(row.out_account_id);
            if (accMap) {
              accMap.set(dayStr, Number(row.out_balance));
            }
          }
        }

        // Process each account
        for (const account of accounts) {
          const dayMap = accountDataMap.get(account.id) || new Map();
          let points: SparklinePoint[] = [];

          if (dayMap.size > 0) {
            points = [...dayMap.entries()]
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([date, value]) => ({ date, value: Math.round(value * 100) / 100 }));
          } else {
            // Fallback: create flat line
            let currentDate = fromStr;
            while (currentDate <= toStr) {
              points.push({ date: currentDate, value: account.balance });
              const d = new Date(currentDate + 'T12:00:00Z');
              d.setUTCDate(d.getUTCDate() + 1);
              currentDate = d.toISOString().split('T')[0];
            }
          }

          const sampledPoints = downsample(points, 30);
          const firstValue = sampledPoints.length > 0 ? sampledPoints[0].value : account.balance;
          const lastValue =
            sampledPoints.length > 0
              ? sampledPoints[sampledPoints.length - 1].value
              : account.balance;
          const changeAmount = lastValue - firstValue;
          const changePercent = firstValue !== 0 ? (changeAmount / Math.abs(firstValue)) * 100 : 0;
          const trend: 'up' | 'down' | 'neutral' =
            changeAmount > 0.01 ? 'up' : changeAmount < -0.01 ? 'down' : 'neutral';

          result.set(account.id, {
            points: sampledPoints,
            trend,
            changePercent: Math.round(changePercent * 10) / 10,
            changeAmount: Math.round(changeAmount * 100) / 100,
          });
        }

        setData(result);
      } catch (error) {
        console.error('Error fetching multiple account sparklines:', error);
        // Fallback
        const fallbackMap = new Map<string, AccountSparklineData>();
        for (const account of accounts) {
          fallbackMap.set(account.id, {
            points: [{ date: new Date().toISOString().split('T')[0], value: account.balance }],
            trend: 'neutral',
            changePercent: 0,
            changeAmount: 0,
          });
        }
        setData(fallbackMap);
      } finally {
        if (!isStale()) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [user, accounts.map((a) => a.id).join(','), periodDays]);

  return { data, loading };
}
